import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Chip,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  Send as SendIcon,
  Build as BuildIcon,
  Flag as PriorityIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { maintenanceAPI } from "../../utils/api";

const MaintenanceRequestForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    category: "",
    subcategory: "",
    location: "",
    description: "",
    priority: "medium",
    contactPhone: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const categoryData = {
    electrical: {
      label: "Electrical",
      subcategories: [
        "bulb",
        "ac",
        "fan",
        "switch",
        "socket",
        "wiring",
        "inverter",
        "generator",
        "other",
      ],
    },
    plumbing: {
      label: "Plumbing",
      subcategories: [
        "tap",
        "pipe",
        "toilet",
        "sink",
        "drainage",
        "water_heater",
        "pump",
        "other",
      ],
    },
    hvac: {
      label: "HVAC",
      subcategories: [
        "air_conditioning",
        "heating",
        "ventilation",
        "duct_cleaning",
        "filter_replacement",
        "thermostat",
        "other",
      ],
    },
    carpentry: {
      label: "Carpentry",
      subcategories: [
        "door",
        "window",
        "furniture",
        "cabinet",
        "shelf",
        "flooring",
        "ceiling",
        "other",
      ],
    },
    painting: {
      label: "Painting",
      subcategories: [
        "wall_painting",
        "ceiling_painting",
        "door_painting",
        "furniture_painting",
        "touch_up",
        "other",
      ],
    },
    cleaning: {
      label: "Cleaning",
      subcategories: [
        "deep_cleaning",
        "carpet_cleaning",
        "window_cleaning",
        "floor_cleaning",
        "waste_disposal",
        "other",
      ],
    },
    security: {
      label: "Security",
      subcategories: [
        "cctv",
        "alarm_system",
        "door_lock",
        "access_control",
        "intercom",
        "security_lighting",
        "other",
      ],
    },
    other: {
      label: "Other",
      subcategories: ["general_maintenance", "miscellaneous"],
    },
  };

  const priorities = [
    {
      value: "low",
      label: "Low Priority",
      color: "success",
      description: "Can wait a few days",
    },
    {
      value: "medium",
      label: "Medium Priority",
      color: "warning",
      description: "Should be addressed soon",
    },
    {
      value: "high",
      label: "High Priority",
      color: "error",
      description: "Urgent attention needed",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "category") {
      // Reset subcategory when category changes
      setFormData({
        ...formData,
        category: value,
        subcategory: "", // Reset subcategory
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await maintenanceAPI.createRequest(formData);
      const request = response.data;

      let successMessage =
        "Maintenance request submitted successfully! You will receive updates on its progress.";

      // Check if auto-assignment occurred
      if (request.autoAssigned && request.assignedWorkerName) {
        successMessage += ` The system has automatically assigned ${request.assignedWorkerName} to handle your ${formData.category} request.`;
      }

      setSuccess(successMessage);

      // Reset form
      setFormData({
        category: "",
        subcategory: "",
        location: "",
        description: "",
        priority: "medium",
        contactPhone: "",
      });

      // Call success callback to refresh stats
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating maintenance request:", error);
      setError(
        error.response?.data?.message ||
          "Failed to submit maintenance request. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center" }}
      >
        <BuildIcon sx={{ mr: 1 }} />
        Submit Maintenance Request
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fill out the form below to submit your maintenance request. Our team
        will review and assign it to the appropriate worker.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Category Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
              >
                {Object.entries(categoryData).map(([key, category]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ textTransform: "capitalize" }}>
                      {category.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Subcategory Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required disabled={!formData.category}>
              <InputLabel>Subcategory</InputLabel>
              <Select
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                label="Subcategory"
              >
                {formData.category &&
                  categoryData[formData.category]?.subcategories.map(
                    (subcategory) => (
                      <MenuItem key={subcategory} value={subcategory}>
                        <Box sx={{ textTransform: "capitalize" }}>
                          {subcategory.replace(/_/g, " ")}
                        </Box>
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>
          </Grid>

          {/* Location */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              name="location"
              label="Location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Building A, Room 205"
              InputProps={{
                startAdornment: (
                  <LocationIcon sx={{ mr: 1, color: "action.active" }} />
                ),
              }}
              helperText="Provide specific location details for easier access"
            />
          </Grid>

          {/* Priority Selection */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PriorityIcon sx={{ mr: 1 }} />
              Priority Level
            </Typography>
            <Grid container spacing={2}>
              {priorities.map((priority) => (
                <Grid item xs={12} sm={4} key={priority.value}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      border: formData.priority === priority.value ? 2 : 1,
                      borderColor:
                        formData.priority === priority.value
                          ? `${priority.color}.main`
                          : "divider",
                      "&:hover": {
                        borderColor: `${priority.color}.main`,
                        boxShadow: 1,
                      },
                    }}
                    onClick={() =>
                      setFormData({ ...formData, priority: priority.value })
                    }
                  >
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Chip
                        label={priority.label}
                        color={priority.color}
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {priority.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={4}
              name="description"
              label="Problem Description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Please describe the issue in detail. Include any relevant information about when it started, what might have caused it, and any safety concerns."
              helperText="Detailed descriptions help our team understand and resolve issues faster"
            />
          </Grid>

          {/* Contact Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="contactPhone"
              label="Contact Phone (Optional)"
              value={formData.contactPhone}
              onChange={handleChange}
              placeholder="e.g., +1 (555) 123-4567"
              helperText="Provide a phone number for urgent updates"
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() =>
                  setFormData({
                    category: "",
                    subcategory: "",
                    location: "",
                    description: "",
                    priority: "medium",
                    contactPhone: "",
                  })
                }
                disabled={loading}
              >
                Clear Form
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SendIcon />
                }
                sx={{ minWidth: 140 }}
              >
                {loading ? "Submitting..." : "Submit Request"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default MaintenanceRequestForm;
